/**
 * Spanish (Spain) widget strings (#1262). Written by hand against the en_US
 * schema defaults; neutral guest-facing register, informal "tú" where a verb
 * form is needed (the register Airbnb and Booking use in Spanish). Every
 * string for every widget lives in this one file so a Spanish speaker can
 * review it in one pass.
 *
 * REVIEW (choices a native reviewer may want to change):
 *   - property-card.rating_reviews  "reseñas" — Airbnb ES says "evaluaciones",
 *     Booking says "comentarios"; "reseñas" is the neutral general term.
 *   - property-card.feature_baths_short  "baño(s)" — the abbreviated slot is
 *     count-neutral in English ("ba"); Spanish has no standard abbreviation,
 *     so the (s) device keeps "1 baño(s)" / "2 baño(s)" grammatical.
 *   - property-card.feature_bedrooms_short  "dorm." — overlay abbreviation
 *     of dormitorios; "hab." (habitaciones) is the common alternative.
 *   - explorer.price_avg_per_night  "de media/noche" — for "avg/night", read
 *     after the amount ("$180 de media/noche"); "prom./noche" is Latin American.
 *   - calendar.error_advance_notice_error  "{{days}} día(s)" — count-neutral, the
 *     English "{{days}}-day" is count-neutral too and the widget substitutes a bare number.
 *   - explorer.price_total  "en total" — "$500 en total"; a bare "total"
 *     also reads fine in Spanish.
 *   - calendar.footer_legend_available  "Disponible para entrada/salida" —
 *     "Available for Check In/Check Out"; "check-in/check-out" is also
 *     widely used in Spain.
 *
 * Placeholders ({{n}}, {{from}}, %1, …) are the same tokens the English
 * defaults carry; the integrity test pins them.
 */
import type { LocaleTable, WidgetStrings } from './tables'

const propertyCard: WidgetStrings<'property-card'> = {
  price_from: 'Desde',
  price_per_night: '/ noche',
  view_property: 'Ver alojamiento',
  feature_guest: 'huésped',
  feature_guests: 'huéspedes',
  feature_bedroom: 'dormitorio',
  feature_bedrooms: 'dormitorios',
  feature_bedrooms_short: 'dorm.',
  feature_studio: 'Estudio',
  feature_bath: 'baño',
  feature_baths: 'baños',
  feature_baths_short: 'baño(s)',
  rating_reviews: 'reseñas',
  photo_previous: 'Foto anterior',
  photo_next: 'Foto siguiente',
  photo_position: 'Foto %1 de %2',
}

export const es_ES: LocaleTable = {
  amenities: {
    showMoreButton: 'Ver más',
  },

  description: {
    showMoreButton: 'Ver más',
  },

  specs: {
    bedrooms: 'Dormitorios',
    bathrooms: 'Baños',
    guests: 'Huéspedes',
    areaSize: 'Superficie',
    studio: 'Estudio',
  },

  calendar: {
    error_disabled_error: 'Lo sentimos, has seleccionado una fecha no disponible',
    error_check_in_min_stay_error: 'No puedes hacer el check-out ese día: este alojamiento requiere una estancia mínima de {{value}}',
    error_select_range_between_unavailable_days_error: 'Selecciona un rango entre días disponibles',
    error_advance_notice_error: 'Requiere reservar con {{days}} día(s) de antelación',
    tip_select_stay: 'Seleccionar {{from}} – {{to}} · {{nights}}',
    tip_night_one: '{{n}} noche',
    tip_night_other: '{{n}} noches',
    header_offer_weekly_and_monthly: '¡Ahorra un {{weekly}}% en estancias semanales y un {{monthly}}% en estancias mensuales!',
    header_offer_weekly: '¡Ahorra un {{weekly}}% en estancias semanales!',
    header_offer_monthly: '¡Ahorra un {{monthly}}% en estancias mensuales!',
    header_local_time: 'Hora local: ',
    footer_taxes_note: 'Impuestos y tasas no incluidos',
    footer_legend_available: 'Disponible para entrada/salida',
    footer_legend_booked: 'Reservado',
    footer_legend_unavailable: 'No disponible o con restricciones, como estancia mínima',
    footer_clear_dates: 'Borrar fechas',
    min_stay_unit_one: 'noche',
    min_stay_unit_other: 'noches',
  },

  'property-card': propertyCard,

  explorer: {
    ...propertyCard,
    price_starting_at: 'A partir de',
    price_night_one: 'noche',
    price_night_other: 'noches',
    price_total: 'en total',
    price_avg_per_night: 'de media/noche',
    price_view_full: 'Ver precio total',
    price_incl_taxes: 'impuestos y tasas incluidos',
    price_excl_taxes: 'sin impuestos',
    price_excl_taxes_fees: 'sin impuestos ni tasas',
    grid_no_results: 'No se han encontrado alojamientos.',
    grid_load_more: 'Cargar más',
    grid_loading: 'Cargando...',
    slider_previous: 'Anterior',
    slider_next: 'Siguiente',
    slider_go_to: 'Ir a la diapositiva %1',
  },

  // #1424: BookingForm.tsx's own guest-facing strings.
  booking: {
    notice_select_dates: 'Selecciona las fechas',
    notice_select_guests: 'Selecciona los huéspedes',
    notice_hosted_checkout_failed: 'No se pudo abrir la página de reserva. Inténtalo de nuevo.',
    notice_generic_error: 'Se ha producido un error',
    notice_coupon_failed: 'No se pudo aplicar el cupón',
    notice_coupon_needs_dates: 'Primero selecciona las fechas y los huéspedes',
    notice_coupon_needs_code: 'Introduce un código de cupón',
    notice_coupon_applied: '¡Cupón aplicado!',
    notice_coupon_invalid: 'El cupón no es válido para estas fechas',
    button_processing: 'Procesando...',
    button_check_availability: 'Consultar disponibilidad',
    button_close: 'Cerrar',
    button_coupon_apply: 'Aplicar',
    button_coupon_applying: 'Aplicando…',
    label_check_in: 'Entrada',
    label_check_out: 'Salida',
    label_check_in_static: 'Entrada',
    label_check_out_static: 'Salida',
    placeholder_add_date: 'Añadir fecha',
    label_guests: 'Huéspedes',
    label_adults: 'Adultos',
    label_children: 'Niños',
    label_infants: 'Bebés',
    label_pets: 'Mascotas',
    hint_adults_age: '13 años o más',
    hint_children_age: 'De 2 a 12 años',
    hint_infants_age: 'Menores de 2 años',
    hint_pets_not_allowed: 'No permitidas en este alojamiento',
    aria_guest_selection: 'Selección de huéspedes',
    aria_decrease_guests: 'Reducir huéspedes',
    aria_increase_guests: 'Aumentar huéspedes',
    aria_decrease_adults: 'Reducir adultos',
    aria_increase_adults: 'Aumentar adultos',
    aria_decrease_children: 'Reducir niños',
    aria_increase_children: 'Aumentar niños',
    aria_decrease_infants: 'Reducir bebés',
    aria_increase_infants: 'Aumentar bebés',
    aria_decrease_pets: 'Reducir mascotas',
    aria_increase_pets: 'Aumentar mascotas',
    unit_guest_one: 'huésped',
    unit_guest_other: 'huéspedes',
    unit_infant_one: 'bebé',
    unit_infant_other: 'bebés',
    unit_pet_one: 'mascota',
    unit_pet_other: 'mascotas',
    guest_max_note: 'Este alojamiento admite un máximo de {{n}} {{unit}}.',
    guest_max_note_with_infants: 'Este alojamiento admite un máximo de {{n}} {{unit}}, sin contar los bebés.',
    unit_night_one: 'noche',
    unit_night_other: 'noches',
    label_min_stay: 'Estancia mínima: {{value}}',
    pill_min_stay: 'Mín. {{n}} noches',
    pill_min_stay_one: 'Mín. 1 noche',
    label_have_coupon: 'Tengo un cupón',
    placeholder_coupon_code: 'Código de cupón',
    label_total_after_tax: 'Total con impuestos',
    label_total_before_taxes: 'Total sin impuestos',
    note_taxes_at_checkout: 'Los impuestos se añaden al finalizar la reserva',
    discount_weekly_and_monthly: '¡Ahorra un {{weekly}}% en estancias semanales y un {{monthly}}% en estancias mensuales!',
    discount_weekly: '¡Ahorra un {{weekly}}% en estancias semanales!',
    discount_monthly: '¡Ahorra un {{monthly}}% en estancias mensuales!',
    dialog_title: 'Reserva',
    label_starting_at: 'Desde ',
    unit_per_night: ' / noche',
    unit_total: ' en total',
    aria_loading_price: 'Cargando el precio',
    calendar_modal_title: 'Selecciona las fechas',
    label_when: 'Cuándo',
    button_clear_dates: 'Borrar fechas',
    label_subtotal: 'Importe parcial:',
    label_total_modal: 'Importe total:',
    aria_charge_explanation: 'Sobre este cargo',
    aria_price_details_dialog: 'Detalles del precio',
    aria_close_price_details: 'Cerrar detalles del precio',
    group_rent: 'Alquiler:',
    group_fees: 'Tarifa(s):',
    group_addons: 'Extra(s):',
    group_others: 'Otros:',
    group_taxes: 'Impuesto(s):',
  },
  // #1529: the checkout page's own guest-facing strings.
  checkout: {
    // Checkout shell
    button_back: '← Volver',
    status_loading_checkout: 'Cargando la página de reserva...',
    button_processing: 'Procesando...',
    button_explore_properties: 'Explorar alojamientos',

    // Errors & notices
    notice_no_booking_selected: 'No has seleccionado ninguna reserva. Ve a la página de un alojamiento para iniciar el proceso de reserva.',
    error_checkout_load_failed: 'No se pudieron cargar los datos de la reserva.',
    error_checkout_data_unavailable: 'No es posible cargar los datos de la reserva.',
    error_load_payment_settings: 'No se pudo obtener la configuración de pago',
    error_load_quote: 'No se pudo obtener el presupuesto',
    error_load_property: 'No se pudo obtener el alojamiento',
    error_reservation_failed: 'No se pudo crear la reserva. Inténtalo de nuevo.',
    error_create_reservation: 'Error al crear la reserva',
    error_hosted_checkout_failed: 'No se pudo abrir la página de reserva. Inténtalo de nuevo.',
    error_hosted_checkout_url: 'Error al obtener la URL de la página de reserva',
    error_addon_update_failed: 'No se pudo actualizar ese extra.',
    error_update_addon: 'Error al actualizar el extra',
    error_payment_session_not_resumed: 'No se pudo reanudar tu sesión de pago. Vuelve a introducir tus datos para completar la reserva.',
    error_threeds_failed: 'La autenticación 3D Secure ha fallado. Inténtalo de nuevo.',
    error_payment_session_wrong_booking: 'Tu sesión de pago pertenecía a otra reserva y no se pudo reanudar. Vuelve a introducir los datos de pago.',

    // Hosted checkout
    heading_complete_your_booking: 'Finaliza tu reserva',
    text_hosted_checkout_intro: 'Este alojamiento utiliza el proceso de pago seguro de {{pms}}. Haz clic a continuación para que te redirijamos y puedas completar tus datos y el pago.',
    label_pms_fallback_host: 'tu anfitrión',
    button_continue_to_pms_checkout: 'Continuar al pago con {{pms}}',
    status_redirecting: 'Redirigiendo...',

    // Guest information
    heading_guest_information: 'Datos del huésped',
    label_first_name: 'Nombre',
    placeholder_first_name: 'Nombre',
    label_last_name: 'Apellidos',
    placeholder_last_name: 'Apellidos',
    label_email: 'Correo electrónico',
    placeholder_email: 'email@ejemplo.com',
    label_phone: 'Teléfono',
    placeholder_phone: 'Número de teléfono',
    error_invalid_phone: 'Introduce un número de teléfono válido.',
    placeholder_survey_select: '— Selecciona —',
    placeholder_survey_other: 'Especifica...',
    hint_all_fields_required: 'Todos los campos son obligatorios',
    heading_mailing_address: 'Dirección postal',
    label_address_line_1: 'Dirección (línea 1)',
    placeholder_address_line_1: 'Calle y número',
    label_address_line_2: 'Dirección (línea 2)',
    placeholder_address_line_2: 'Piso, puerta, escalera, etc.',
    label_city: 'Ciudad',
    label_state: 'Provincia',
    placeholder_state: 'Estado / Provincia',
    label_zip_code: 'Código postal',
    placeholder_zip_code: 'Código postal',
    label_country: 'País',
    placeholder_select_country: 'Selecciona un país',

    // Agreements & consent
    heading_rental_agreement: 'Contrato de alquiler',
    text_acceptance_privacy_and_terms: 'He leído y acepto las Normas de la casa y la Política de cancelación anteriores, así como la {{privacy}} y los {{terms}}.',
    text_acceptance_terms_only: 'He leído y acepto las Normas de la casa y la Política de cancelación anteriores, así como los {{terms}}.',
    text_acceptance_privacy_only: 'He leído y acepto las Normas de la casa y la Política de cancelación anteriores, así como la {{privacy}}.',
    text_acceptance_no_links: 'He leído y acepto las Normas de la casa y la Política de cancelación anteriores.',
    label_privacy_policy: 'Política de privacidad',
    label_terms: 'Términos',
    label_signature_draw: 'Firma aquí',
    label_signature_name: 'Firma escribiendo tu nombre completo',
    label_signature_initials: 'Escribe tus iniciales para firmar',
    button_clear_signature: 'Borrar firma',
    aria_close_consent_dialog: 'Cerrar',

    // Payment form
    heading_payment_information: 'Información de pago',
    status_loading_payment_form: 'Cargando el formulario de pago...',
    label_name_on_card: 'Nombre en la tarjeta',
    placeholder_name_on_card: 'Nombre en la tarjeta',
    label_card_number: 'Número de tarjeta',
    label_card_expiry: 'Caducidad',
    placeholder_card_expiry: 'MM/AA',
    label_card_cvc: 'CVC',
    text_powered_by: 'Con la tecnología de',
    error_payment_processor_load_failed: 'No se pudo cargar la pasarela de pago',
    error_card_form_load_failed: 'No se pudo cargar el formulario de tarjeta. Actualiza la página e inténtalo de nuevo.',
    error_payment_processor_not_ready: 'La pasarela de pago aún no está lista',
    error_cvc_check_failed: 'La verificación del código de seguridad (CVC) de la tarjeta ha fallado. Vuelve a comprobar los datos de tu tarjeta.',
    error_payment_processing_failed: 'No se ha podido procesar el pago',
    error_payment_setup_intent: 'No se pudo iniciar la verificación de la tarjeta',
    error_guestypay_load_failed: 'No se pudo cargar GuestyPay',
    error_guestypay_unavailable: 'La tokenización de GuestyPay no está disponible',
    error_payment_form_load_failed: 'No se pudo cargar el formulario de pago',
    error_payment_failed: 'El pago ha fallado',
    error_enter_card_details: 'Introduce los datos de tu tarjeta',

    // Booking summary
    heading_booking_summary: 'Resumen de la reserva',
    label_check_in: 'Entrada',
    label_check_out: 'Salida',
    label_guests: 'Huéspedes',
    label_nights: 'Noches',
    label_total: 'Total',
    group_rent: 'Alquiler:',
    group_fees: 'Tarifa(s):',
    group_addons: 'Extra(s):',
    group_others: 'Otros:',
    group_taxes: 'Impuesto(s):',
    label_taxes: 'Impuestos',

    // Property & policies
    heading_house_rules: 'Normas de la casa',
    heading_cancellation_policy: 'Política de cancelación',
    heading_payment_schedule: 'Calendario de pagos',
    text_payment_term_at_booking: 'al reservar',
    label_payment_term_before: 'antes de',
    label_payment_term_after: 'después de',
    text_payment_term_timing_relative: '{{count}} {{unit}} {{relation}} {{event}}',
    text_payment_term_line: '{{amount}} {{timing}}',

    // Confirmation screen
    heading_inquiry_sent: '¡Consulta enviada!',
    heading_request_sent: '¡Solicitud de reserva enviada!',
    heading_booking_confirmed: '¡Reserva confirmada!',
    text_inquiry_sent_body: 'Tu consulta se ha enviado al gestor del alojamiento. Se pondrá en contacto contigo en breve.',
    text_request_sent_body: 'Tu solicitud de reserva se ha enviado. El anfitrión la revisará y confirmará tu reserva.',
    text_booking_confirmed_body: 'Tu reserva se ha confirmado. Recibirás un correo de confirmación en breve.',
    label_reservation_id: 'ID de reserva:',
    notice_payment_required: 'Tu reserva requiere un pago. Completa el pago para confirmar la reserva.',
    button_complete_payment: 'Completar el pago',
    button_go_back: 'Volver',
  },

  // #1529: the confirmation page's own guest-facing strings. The payment
  // block repeats checkout's wording on purpose — the same leaves render on
  // both pages and each host resolves against its own table entry.
  confirmation: {
    // Page states
    status_loading_reservation: 'Cargando los detalles de la reserva...',
    error_invalid_link: 'Lo sentimos, el enlace que has seguido no es válido o ha caducado.',
    error_reservation_unavailable: 'No se han podido cargar los datos de la reserva.',
    error_fetch_reservation_failed: 'No se ha podido cargar la reserva',

    // Success modal
    heading_modal_inquiry_sent: '¡Consulta enviada!',
    heading_modal_request_sent: '¡Solicitud de reserva enviada!',
    heading_modal_booking_confirmed: '¡Reserva confirmada!',
    text_modal_message_inquiry: 'Tu consulta se ha enviado al gestor del alojamiento. Se pondrá en contacto contigo en breve.',
    text_modal_message_request: 'Tu solicitud de reserva se ha enviado. El anfitrión revisará y confirmará tu reserva.',
    text_modal_message_booking: 'Tu reserva se ha confirmado. Recibirás un correo de confirmación en breve.',
    aria_modal_close: 'Cerrar',
    button_modal_got_it: '¡Entendido!',

    // Reservation summary
    label_row_type: 'Tipo',
    label_row_status: 'Estado',
    label_row_checkin: 'Entrada',
    label_row_checkout: 'Salida',
    label_row_guests: 'Huéspedes',
    label_type_inquiry: 'Consulta',
    label_type_booking: 'Reserva',
    label_type_request: 'Solicitud de reserva',
    label_status_received: 'Recibida',
    label_status_pending: 'Pendiente',
    label_status_confirmed: 'Confirmada',
    label_status_booked: 'Reservada',
    label_status_requested: 'Solicitada',
    label_status_closed: 'Cerrada',

    // Cost breakdown
    heading_cost_breakdown: 'Desglose del precio',
    label_total: 'Total',
    label_taxes: 'Impuestos',
    group_rent: 'Alquiler:',
    group_fees: 'Tarifa(s):',
    group_addons: 'Extra(s):',
    group_others: 'Otros:',
    group_taxes: 'Impuesto(s):',

    // Payment recovery
    notice_payment_method_failed: 'Tu método de pago anterior ha fallado. Introduce los datos de un nuevo método de pago.',
    button_pay_now: 'Pagar ahora',
    status_completing_payment: 'Completando tu pago…',
    button_submitting: 'Enviando…',
    button_submit_payment: 'Enviar pago',
    text_payment_recovery_success: 'Gracias: tus datos de pago se han enviado correctamente.',
    error_payment_submit_failed: 'No se pudo enviar el pago. Inténtalo de nuevo.',
    error_threeds_auth_failed: 'La autenticación 3D Secure ha fallado. Inténtalo de nuevo.',
    error_threeds_resume_failed: 'No se pudo reanudar tu sesión de pago. Vuelve a introducir tus datos de pago.',
    error_threeds_recovery_unavailable: 'Tu tarjeta se ha autenticado, pero el pago no se ha podido completar en esta página. Ponte en contacto con nosotros para finalizar el pago.',

    // Payment form (shared with Checkout)
    heading_payment_information: 'Información de pago',
    status_loading_payment_form: 'Cargando el formulario de pago...',
    label_name_on_card: 'Nombre en la tarjeta',
    placeholder_name_on_card: 'Nombre en la tarjeta',
    label_card_number: 'Número de tarjeta',
    label_card_expiry: 'Caducidad',
    placeholder_card_expiry: 'MM/AA',
    label_card_cvc: 'CVC',
    text_powered_by: 'Con la tecnología de',

    // Payment form errors (shared with Checkout)
    error_payment_processor_load_failed: 'No se pudo cargar la pasarela de pago',
    error_payment_form_load_failed: 'No se pudo cargar el formulario de pago',
    error_card_form_load_failed: 'No se pudo cargar el formulario de tarjeta. Actualiza la página e inténtalo de nuevo.',
    error_payment_processor_not_ready: 'La pasarela de pago aún no está lista',
    error_guestypay_unavailable: 'La tokenización de GuestyPay no está disponible',
    error_enter_card_details: 'Introduce los datos de tu tarjeta',
    error_cvc_check_failed: 'La verificación del código de seguridad (CVC) de la tarjeta ha fallado. Vuelve a comprobar los datos de tu tarjeta.',
    error_payment_processing_failed: 'No se ha podido procesar el pago',
    error_payment_failed: 'El pago ha fallado',
    error_payment_setup_intent: 'No se pudo iniciar la verificación de la tarjeta',
    error_guestypay_load_failed: 'No se pudo cargar GuestyPay',
  },
}
